import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import CreatePostModal from './CreatePostModal';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Global listener for `lumera:milestone-share` events. When a user hits a
 * milestone (finished an audio chapter, a reading-plan day, or the daily
 * Sundae) it surfaces a gentle toast that opens the create-story dialog,
 * pre-filled where relevant. Only nudges signed-in users.
 */
const MilestoneShareNudge = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const es = language === 'es';
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({ tab: 'text', verseText: '', reference: '', imageBlob: null });

  const openStory = useCallback((detail) => {
    if (detail.imageBlob) {
      setPrefill({ tab: 'photo', verseText: '', reference: '', imageBlob: detail.imageBlob });
    } else {
      const hasVerse = !!detail.reference;
      setPrefill({
        tab: hasVerse ? 'verse' : 'text',
        verseText: detail.verseText || detail.message || '',
        reference: detail.reference || '',
        imageBlob: null,
      });
    }
    // Clear any lingering toasts (e.g. "Prayer answered!" and this action's
    // own toast) so they never float above and intercept the modal controls.
    toast.dismiss();
    // Close any open Radix Sheet (e.g. MessagesInbox) first so the global
    // create-post Dialog is never trapped behind a Sheet overlay / body
    // pointer-events:none. Wait for the sheet's exit animation before opening.
    window.dispatchEvent(new CustomEvent('lumera:close-inbox'));
    setTimeout(() => setOpen(true), 280);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!user) return;
      const detail = e.detail || {};
      // When the caller already captured share intent (e.g. an in-Sheet
      // "Share my milestone" button), open the composer directly instead of
      // surfacing another toast that could be trapped behind a modal.
      if (detail.direct) { openStory(detail); return; }
      toast(detail.message || (es ? '¡Buen trabajo!' : 'Nicely done!'), {
        duration: 8000,
        action: {
          label: es ? 'Compartir en tu historia' : 'Share to your story',
          onClick: () => openStory(detail),
        },
      });
    };
    window.addEventListener('lumera:milestone-share', handler);
    return () => window.removeEventListener('lumera:milestone-share', handler);
  }, [user, es, openStory]);

  if (!user) return null;

  return (
    <CreatePostModal
      open={open}
      onClose={() => setOpen(false)}
      language={language}
      defaultDestination="story"
      allowDestinationChoice={true}
      initialTab={prefill.tab}
      initialVerseText={prefill.verseText}
      initialReference={prefill.reference}
      initialImageBlob={prefill.imageBlob}
    />
  );
};

export default MilestoneShareNudge;
